import { useTranslation } from "react-i18next";

import { socialMediaLinks } from "simplydo/core/users";
import {
  BRAND_ICONS,
  LinkedInIcon,
  MSTeamsIcon,
} from "simplydo/react/ui/brand-icon";
import type { IconData } from "simplydo/react/ui/icon";

import { useCurrentUser } from "utils/currentUser";

import type { ChipUser, SessionUser } from "./types";

const PROFILE_SOCIAL_KEYS = [
  "linkedin",
  "website",
  "facebook",
  "instagram",
  "github",
  "twitter",
] as const;

export type ChipSocialLink = {
  key: string;
  label: string;
  href: string;
  color: string;
  icon?: IconData;
  glyph?: string;
  internal?: boolean;
};

export const useSocialLinks = (user?: ChipUser | null): ChipSocialLink[] => {
  const { t } = useTranslation();
  const currentUser = useCurrentUser() as SessionUser | null;
  if (!user?.profile) return [];

  const org = currentUser?.ownerOrganisation;
  const links: ChipSocialLink[] = [];

  if (
    !user.profile.linkedin &&
    currentUser?._id === user._id &&
    org?.superadminTags?.innovationType === "externalInnovation"
  ) {
    links.push({
      key: "addLinkedin",
      label: t("users.chip.addLinkedin"),
      href: "/preferences/profile",
      color: "#8a8e90",
      icon: LinkedInIcon,
      internal: true,
    });
  }

  PROFILE_SOCIAL_KEYS.forEach((key) => {
    const link = user.profile?.[key];
    if (!link) return;
    const config = socialMediaLinks[key];
    links.push({
      key,
      label: config.content,
      href: link.startsWith("https://")
        ? link
        : link.startsWith("www.")
          ? `https://${link}`
          : config.link + link,
      color: config.color,
      icon:
        "brand" in config
          ? BRAND_ICONS[config.brand]
          : "icon" in config
            ? config.icon
            : undefined,
      glyph: "iconLeftAlt" in config ? config.iconLeftAlt : undefined,
    });
  });

  if (
    user.teamsHandle &&
    org?.enabledFeatures?.includes("teamsHandleAdvertising")
  ) {
    links.push({
      key: "teams",
      label: t("users.chip.messageViaTeams"),
      href: `https://teams.microsoft.com/l/chat/0/0?users=${encodeURIComponent(user.teamsHandle)}`,
      color: "#6264A7",
      icon: MSTeamsIcon,
    });
  }

  return links;
};
